<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import EnNavbarSearchField from 'Components/inputs/EnNavbarSearch';
import EnNotificationsMenu from 'Components/notifications/EnNotificationsMenu/EnNotificationsMenu.vue';
import Breadcrumbs from 'Components/navigation/EnBreadcrumb/EnBreadcrumbs.vue';
import EnIcon from 'Components/icons/EnIcon.vue';
import EnHelpMenu from 'Components/navigation/EnHelpMenu/EnHelpMenu.vue';
import { useSessionStorage } from 'Components/navigation/utils/useSessionStorage';
import { useUserSettingsStore } from 'Js/store/useUserSettingsStore';
import { useRoute } from 'vue-router';

const props = defineProps({
  /**
   * Flag to indicate whether it is in development environment.
   */
  isDevelopment: {
    type: Boolean,
    default: false
  },
  /**
   * Search query for the navbar.
   */
  searchQuery: {
    type: String,
    default: ''
  },
  /**
   * Breadcrumbs to display in the navbar.
   */
  breadcrumbs: {
    type: Array,
    default: () => []
  },
  /**
   * Flag to enable production view in development environment.
   */
  isProdViewEnabled: {
    type: Boolean,
    default: false
  },
  /**
   * Whether the search bar is visible or not.
   */
  isSearchEnabled: {
    type: Boolean,
    default: true
  },
  /**
   * Whether the help panel is visible or not.
   */
  isHelpEnabled: {
    type: Boolean,
    default: true
  },
  /**
   * Check if current page is EN.Auth. ToDo: remove after SPA
   */
  isAuthUrl: {
    type: Boolean,
    default: false
  },
  /**
   * EN.Gine base url
   */
  engineUrl: {
    type: String
  },
  /**
   * Check if current user is authenticated. ToDo: remove after SPA
   */
  isLoggedIn: {
    type: Boolean,
    default: false
  },
});

const stateStore = storeToRefs(useUserSettingsStore());
const route = useRoute();
const {getIsEnTab} = useSessionStorage();
const isEnTab = computed(() => {
  return route?.name === getIsEnTab();
});

const emit = defineEmits(['update:searchQuery']);
const localSearch = ref(props.searchQuery);

const environmentClass = computed(() => {
  switch (true) {
    case props.isProdViewEnabled:
      return '';
    case stateStore.state.value?.IsLocalApp:
      return 'navbar-local';
    case stateStore.state.value?.IsDev:
      return 'navbar-dev';
    default:
      return '';
  }
});

watch(() => props.searchQuery, v => {
  if (v !== localSearch.value) localSearch.value = v;
});
watch(localSearch, v => emit('update:searchQuery', v));

</script>

<template>
  <div class="en-top-navbar" :class="[environmentClass, { 'login-navbar': !isLoggedIn }]">
    <div class="en-account-settings" v-if="!isLoggedIn">
      <div class="branding-click-target" role="button" tabindex="0" aria-label="Home" href="/">
        <en-icon size="30" class="en-icon icon-32 icon-enloc_logo_min"></en-icon>
      </div>
    </div>
    <div class="en-breadcrumb-list" v-if="isLoggedIn">
      <breadcrumbs :items="breadcrumbs">
        <template v-for="(slot, name) in $slots" :key="name" v-slot:[name]="slotProps">
          <slot :name="name" v-bind="slotProps"></slot>
        </template>
      </breadcrumbs>
    </div>
    <div class="right-section d-flex">
      <en-navbar-search-field 
          v-model="localSearch"
          v-if="isLoggedIn && !isEnTab && isSearchEnabled"
          :engine-url="engineUrl"
          :is-auth-url="isAuthUrl"
      ></en-navbar-search-field>
      <div class="en-help-menu d-flex" v-if="isLoggedIn && !isEnTab && isHelpEnabled">
        <en-help-menu></en-help-menu>
      </div>
      <div class="en-notification d-flex" v-if="isLoggedIn">
        <en-notifications-menu></en-notifications-menu>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";
@use "Styles/helper/mixins.scss";

.en-top-navbar {
  background: variables.$en-blue-2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  // Same side padding as the page below, set by EnNavigationBar, so the breadcrumbs line up with the page content.
  padding: 0.5rem var(--en-page-inline-padding, 40px);
  height: variables.$navigation-bar-height;

  &.login-navbar {
    padding-left: 0;

    .en-account-settings {
      display: flex;
      overflow: hidden;
      height: 56px;
      padding: 6px 20px 6px 010px;
      background: variables.$en-blue;
      align-items: center;
      gap: 1rem;
      justify-content: center;
      color: variables.$en-red;

      .branding-click-target {
        width: 100%;
        justify-content: center;
      }

      .branding-click-target {
        display: flex;
        align-items: center;
        gap: 1rem;
        color: inherit;
        cursor: pointer;

        .en-icon {
          flex-shrink: 0;
        }
      }
    }
  }

  .en-breadcrumb-list {
    min-width: 0;
  }

  .right-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 20px;
    flex: 1;
  }
}

.en-top-navbar.navbar-dev {
  background: variables.$en-red;

  .en-breadcrumb-list {
    --en-breadcrumb-muted: rgb(255 255 255 / 70%);
  }
}

.en-top-navbar.navbar-local {
  background: variables.$en-dark-teal;
}

#toggler {
  button {
    background: variables.$en-0;
    border-radius: 3px;
    padding: 2px;
  }

  .toggler-icon {
    color: variables.$en-blue;
  }
}
</style>
